/**
 * Platform-specific specifications for social media publishing
 * Used for preview rendering, validation, and image handling
 */

export const PLATFORM_SPECS = {
  linkedin: {
    name: 'LinkedIn',
    characterLimit: 3000,
    imageSpecs: {
      width: 1200,
      height: 627,
      ratio: '1.91:1',
      aspectRatio: 1.91,
      maxFileSize: 5 * 1024 * 1024, // 5MB
    },
    hashtagLimit: 5,
    features: ['mentions', 'hashtags', 'links'],
    warningThreshold: 2700, // 90% of limit
    previewTruncateAt: 210, // LinkedIn shows "...see more" around this point
    colors: {
      primary: '#0A66C2',
      secondary: '#00000099',
      background: '#FFFFFF',
      border: '#E0E0E0',
    },
  },
  facebook: {
    name: 'Facebook',
    characterLimit: 63206,
    imageSpecs: {
      width: 1200,
      height: 630,
      ratio: '1.91:1',
      aspectRatio: 1.91,
      maxFileSize: 4 * 1024 * 1024, // 4MB
    },
    hashtagLimit: null, // Unlimited but discouraged
    features: ['mentions', 'hashtags', 'links', 'emoji'],
    warningThreshold: 500, // Best practice limit for engagement
    previewTruncateAt: 477, // Facebook shows "See More" around here
    colors: {
      primary: '#1877F2',
      secondary: '#65676B',
      background: '#FFFFFF',
      border: '#DADDE1',
    },
  },
  x: {
    name: 'X',
    characterLimit: 280,
    imageSpecs: {
      width: 1200,
      height: 675,
      ratio: '16:9',
      aspectRatio: 16 / 9,
      maxFileSize: 5 * 1024 * 1024, // 5MB
    },
    hashtagLimit: 2, // Best practice
    features: ['mentions', 'hashtags', 'threads'],
    warningThreshold: 250,
    previewTruncateAt: 280, // Hard limit
    supportsThreads: true,
    colors: {
      primary: '#000000',
      secondary: '#536471',
      background: '#FFFFFF',
      border: '#EFF3F4',
      accent: '#1D9BF0',
    },
  },
  instagram: {
    name: 'Instagram',
    characterLimit: 2200,
    imageSpecs: {
      width: 1080,
      height: 1080,
      ratio: '1:1',
      aspectRatio: 1,
      maxFileSize: 8 * 1024 * 1024, // 8MB
    },
    hashtagLimit: 30,
    features: ['hashtags', 'mentions'],
    warningThreshold: 2000,
    previewTruncateAt: 125, // Instagram shows "more" early
    colors: {
      primary: '#000000',
      secondary: '#8E8E8E',
      background: '#FFFFFF',
      border: '#DBDBDB',
      gradient: ['#F58529', '#DD2A7B', '#8134AF', '#515BD4'],
    },
  },
};

/**
 * Get platform specs by type
 * @param {string} platformType - 'linkedin' | 'facebook' | 'x' | 'instagram'
 * @returns {Object} Platform specifications
 */
export const getPlatformSpecs = (platformType) => {
  return PLATFORM_SPECS[platformType?.toLowerCase()] || null;
};

/**
 * Get character limit status
 * @param {number} charCount - Current character count
 * @param {string} platformType - Platform type
 * @returns {'ok' | 'warning' | 'error'} Status
 */
export const getCharacterStatus = (charCount, platformType) => {
  const specs = getPlatformSpecs(platformType);
  if (!specs) return 'ok';

  if (charCount > specs.characterLimit) return 'error';
  if (charCount >= specs.warningThreshold) return 'warning';
  return 'ok';
};

/**
 * Parse thread content - extracts clean tweets from various AI generation formats
 * Supports: ---TWEET--- delimiters, TWEET X/Y headers, numbered tweets
 * @param {string} content - Content to parse
 * @param {number} limit - Character limit per tweet (default 280)
 * @returns {string[]} Array of tweet chunks
 */
export const splitIntoThreads = (content, limit = 280) => {
  if (!content || typeof content !== 'string') return [];

  // Try to extract tweets from structured format first
  const extractedTweets = extractTweetsFromContent(content);
  if (extractedTweets.length > 0) {
    // Ensure each tweet is under limit, split if needed
    return extractedTweets.flatMap((tweet) => splitByWords(tweet, limit));
  }

  // Fallback: if content fits in one tweet, return as is
  if (content.length <= limit) return [content];

  // Otherwise, use word-based splitting
  return splitByWords(content, limit);
};

/**
 * Extract clean tweet content from AI-generated format
 * Handles multiple formats: styled divs, bold headers, ---TWEET---, plain headers
 * @param {string} content - Raw content with metadata
 * @returns {string[]} Array of clean tweet texts
 */
const extractTweetsFromContent = (content) => {
  // Format 1: Styled div cards with <strong>Tweet N</strong> and <p>content</p>
  if (/<div[^>]*>[\s\S]*?<strong[^>]*>Tweet\s*\d+<\/strong>/i.test(content)) {
    const tweets = [];
    // Match the paragraph content inside each card
    const regex = /<strong[^>]*>Tweet\s*\d+<\/strong>[\s\S]*?<p[^>]*>([\s\S]*?)<\/p>/gi;
    let match;
    while ((match = regex.exec(content)) !== null) {
      const tweetText = match[1]
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .trim();
      if (tweetText) tweets.push(tweetText);
    }
    if (tweets.length > 0) return tweets;
  }

  // Format 2: Bold headers **Tweet N** or <strong>Tweet N</strong>
  const boldHeaderRegex = /(?:\*\*Tweet\s*\d+\*\*|<(?:strong|b)>Tweet\s*\d+<\/(?:strong|b)>)/i;
  if (boldHeaderRegex.test(content)) {
    const parts = content.split(/(?:\*\*Tweet\s*\d+\*\*|<(?:strong|b)>Tweet\s*\d+<\/(?:strong|b)>)/i);
    return parts
      .slice(1)
      .map((part) => {
        return part
          .replace(/<br\s*\/?>/gi, ' ')
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      })
      .filter((t) => t.length > 0 && t.length < 500);
  }

  // Format 3: ---TWEET--- delimiter
  if (content.includes('---TWEET---')) {
    return content
      .split('---TWEET---')
      .map((t) => cleanTweetText(t))
      .filter((t) => t.length > 0);
  }

  // Format 4: Plain "Tweet N" headers
  if (/Tweet\s*\d+/i.test(content)) {
    const parts = content.split(/Tweet\s*\d+[:\s]*/i);
    const tweets = parts
      .slice(1)
      .map((part) => cleanTweetText(part))
      .filter((t) => t.length > 0 && t.length < 500);
    if (tweets.length > 1) return tweets;
  }

  return []; // No structured format detected
};

/**
 * Extract tweet body content, stopping at metadata markers
 * @param {string} text - Text after a TWEET header
 * @returns {string} Clean tweet body
 */
const extractTweetBody = (text) => {
  const lines = text.split('\n');
  const tweetLines = [];

  for (const line of lines) {
    // Stop at metadata markers
    if (isMetadataLine(line)) continue;
    // Stop at character count indicators
    if (/^\d+\s*characters?$/i.test(line.trim())) continue;
    // Stop at next tweet header
    if (/^TWEET\s*\d+/i.test(line)) break;

    const trimmed = line.trim();
    if (trimmed) tweetLines.push(trimmed);
  }

  return tweetLines.join('\n');
};

/**
 * Check if a line is metadata/strategy content (not tweet content)
 * @param {string} line - Line to check
 * @returns {boolean} True if line is metadata
 */
const isMetadataLine = (line) => {
  const trimmed = line.trim().toLowerCase();
  const metadataPatterns = [
    /^[•\-\*]\s*(engagement|hook|formatting|strategy|tone|cta|call.to.action)/i,
    /^(engagement|hook|formatting|strategy|tone|cta):/i,
    /^📱|^🎯|^💡|^🔥|^✨|^📊/,
    /^\d+\s*characters?$/i,
    /^thread\s*(flow|structure|strategy)/i,
  ];
  return metadataPatterns.some((pattern) => pattern.test(trimmed));
};

/**
 * Clean tweet text - remove extra whitespace, metadata artifacts
 * @param {string} text - Raw tweet text
 * @returns {string} Cleaned tweet text
 */
const cleanTweetText = (text) => {
  return text
    .split('\n')
    .filter((line) => !isMetadataLine(line))
    .join('\n')
    .replace(/^\d+\s*characters?\s*/gim, '') // Remove character counts
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Split content by words, respecting character limit
 * @param {string} content - Content to split
 * @param {number} limit - Character limit per tweet (default 280)
 * @returns {string[]} Array of tweet chunks
 */
const splitByWords = (content, limit = 280) => {
  if (content.length <= limit) return [content];

  const words = content.split(/\s+/);
  const threads = [];
  let currentThread = '';

  words.forEach((word) => {
    const testThread = currentThread ? `${currentThread} ${word}` : word;

    if (testThread.length <= limit) {
      currentThread = testThread;
    } else {
      if (currentThread) threads.push(currentThread);

      // Handle words longer than limit (rare)
      if (word.length > limit) {
        threads.push(word.substring(0, limit - 3) + '...');
        currentThread = '...' + word.substring(limit - 3);
      } else {
        currentThread = word;
      }
    }
  });

  if (currentThread) threads.push(currentThread);

  return threads;
};

/**
 * Check if content is in a structured thread format
 * @param {string} content - Content to check
 * @returns {boolean} True if content has thread structure
 */
export const isThreadFormat = (content) => {
  if (!content) return false;
  // Check for various thread formats
  return (
    /(?:\*\*Tweet\s*\d+\*\*|<(?:strong|b)>Tweet\s*\d+<\/(?:strong|b)>)/i.test(content) ||
    /<h3>Tweet\s*\d+<\/h3>/i.test(content) ||
    content.includes('---TWEET---') ||
    /Tweet\s*\d+/i.test(content)
  );
};

/**
 * Calculate image crop dimensions for platform
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {string} platformType - Platform type
 * @returns {Object} Crop dimensions { x, y, width, height }
 */
export const calculateCropDimensions = (originalWidth, originalHeight, platformType) => {
  const specs = getPlatformSpecs(platformType);
  if (!specs) return { x: 0, y: 0, width: originalWidth, height: originalHeight };

  const targetRatio = specs.imageSpecs.aspectRatio;
  const originalRatio = originalWidth / originalHeight;

  let cropWidth, cropHeight, x, y;

  if (originalRatio > targetRatio) {
    // Image is wider than target - crop sides
    cropHeight = originalHeight;
    cropWidth = originalHeight * targetRatio;
    x = (originalWidth - cropWidth) / 2;
    y = 0;
  } else {
    // Image is taller than target - crop top/bottom
    cropWidth = originalWidth;
    cropHeight = originalWidth / targetRatio;
    x = 0;
    y = (originalHeight - cropHeight) / 2;
  }

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(cropWidth),
    height: Math.round(cropHeight),
  };
};

export default PLATFORM_SPECS;
